import { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthContext } from "../Context/AuthContext";
import { getFeatures } from "./subscriptionApi";
import { orgScope } from "../../services/orgScope";

/**
 * Org feature/limit entitlements loaded once after auth from /subscription/features.
 *
 * Default-allow until loaded so the sidebar / guards never hide modules during
 * the initial fetch (grandfathered orgs have everything anyway). Once loaded,
 * `isEnabled` reflects the org's real plan.
 */
const FeaturesContext = createContext({
  features: {},
  limits: {},
  plan: null,
  status: null,
  loaded: false,
  isEnabled: () => true,
  refresh: () => {},
});

export function FeaturesProvider({ children }) {
  const { user } = useContext(AuthContext) || {};
  const scope = user ? orgScope() : "signed-out";
  const query = useQuery({
    queryKey: ["subscription-features", scope],
    queryFn: getFeatures,
    enabled: Boolean(user) && scope !== "anonymous",
    staleTime: 60_000,
    retry: 1,
  });

  const refresh = useCallback(() => query.refetch(), [query]);

  const state = useMemo(() => {
    const d = query.data;
    const loaded = !user || query.isSuccess || query.isError || scope === "anonymous";
    return {
      features: d?.features || {},
      limits: d?.limits || {},
      plan: d?.plan || null,
      status: d?.status || null,
      trial_ends_at: d?.trial_ends_at || null,
      next_billing_date: d?.next_billing_date || null,
      loaded,
    };
  }, [query.data, query.isSuccess, query.isError, user, scope]);

  const isEnabled = useCallback(
    (key) => {
      if (!key) return true;
      if (!state.loaded) return true;
      return Boolean(state.features[key]);
    },
    [state]
  );

  return (
    <FeaturesContext.Provider value={{ ...state, isEnabled, refresh }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export const useFeatures = () => useContext(FeaturesContext);
export const useFeature = (key) => useContext(FeaturesContext).isEnabled(key);
